/**
 * Shaping for the owner's Analytics page (get_owner_analytics,
 * get_owner_pricing and get_owner_records in
 * supabase/migrations/20261026150000_owner_analytics.sql).
 *
 * The SQL defines every number; this module only arranges them. Each
 * headline carries a DrillSpec so the page can list the records it was built
 * from - the same rows, through the same SQL definition.
 */

export interface Speed {
  answered: number;
  median_minutes: number | null;
  pct_within_1h: number | null;
  pct_within_4h: number | null;
}

export interface WindowSummary {
  from: string;
  to: string;
  revenue: number;
  orders: number;
  lines: number;
  customers: number;
  rfqs: number;
  answered: number;
  via_system: number;
  via_manual: number;
  office_rfqs: number;
  after_hours: number;
  speed: Speed;
  top_customer: { customer_key: string; name: string; revenue: number; share: number } | null;
  top2_share: number | null;
}

type WindowKey = 'trailing' | 'prior' | 'qtd' | 'qtd_last_year' | 'range';

interface QuarterRow {
  quarter: string;
  pre_go_live: boolean;
  revenue: number | null;
  orders: number | null;
  lines: number | null;
  customers: number | null;
  new_customers: number | null;
  new_customer_revenue: number | null;
  top2_share: number | null;
  rfqs: number;
  answered: number;
  via_system: number;
  via_manual: number;
  after_hours: number;
  speed: Speed;
}

interface MonthRow {
  month: string;
  revenue: number | null;
  orders: number | null;
  rfqs: number;
  answered: number;
  via_system: number;
  via_manual: number;
  after_hours: number;
  speed: Speed;
  speed_system: Speed;
  speed_manual: Speed;
  purchases: number | null;
}

export interface CustomerRow {
  customer_key: string;
  name: string;
  rfq_family: string | null;
  rfqs: number;
  answered: number;
  via_system: number;
  orders: number;
  lines: number;
  revenue: number | null;
  avg_order: number | null;
  first_order: string | null;
  last_order: string | null;
  last_rfq: string | null;
  speed: Speed | null;
  quarterly: Array<{ quarter: string; revenue: number | null }> | null;
}

interface QuietCustomer {
  customer_key: string;
  name: string;
  first_order: string;
  last_order: string;
  lifetime_orders: number;
  lifetime_revenue: number;
  revenue_12m_before_last: number | null;
  last_rfq: string | null;
}

interface ConversionRow {
  customer_key: string;
  name: string;
  quarters: Array<{ quarter: string; rfqs: number; answered: number; orders: number }> | null;
}

type WristWinRate =
  | { available: false; reason: 'not_built' | 'empty' }
  | {
      available: true;
      linked_pos: number;
      refs_since: string | null;
      refs_through: string | null;
      rfqs: number;
      answered: number;
      won: number;
      won_answered: number;
      quarterly: Array<{ quarter: string; answered: number; won: number }>;
    };

interface ItemRow {
  item_key: string;
  item_name: string;
  model_number: string | null;
  category: string;
  revenue: number;
  quantity: number | null;
  lines: number;
  orders: number;
  customers: number;
}

interface CategoryRow {
  category: string;
  revenue: number;
  lines: number;
  orders: number;
  share: number | null;
}

export interface HeatCell {
  dow: number; // ISO: 1 = Monday
  hour: number; // 0-23 Singapore
  n: number;
  via_system?: number;
}

export interface OwnerAnalytics {
  generated_at: string;
  range: { from: string; to: string; today: string; current_quarter: string };
  summary: Record<WindowKey, WindowSummary>;
  quarterly: QuarterRow[];
  monthly: MonthRow[];
  customers: CustomerRow[];
  quiet_customers: { total: number; items: QuietCustomer[] };
  conversion: ConversionRow[];
  wrist_win_rate: WristWinRate;
  items: ItemRow[];
  categories: CategoryRow[];
  heatmap: { received: HeatCell[]; replied: HeatCell[] };
  costs: {
    total: number;
    documents: number;
    lines: number;
    suppliers: Array<{ supplier: string; total: number; documents: number; last: string }>;
    excluded: Partial<Record<PurchaseExclusion, { lines: number; value: number | null }>>;
  };
  coverage: {
    threads_since: string | null;
    threads_last: string | null;
    threads_pre_go_live: number;
    replies_since: string | null;
    mailbox_scanned_through: string | null;
    system_rfqs_since: string | null;
    system_rfqs_last: string | null;
    orders_since: string | null;
    orders_last: string | null;
    orders_good_from: string;
    orders_extracted_through: string | null;
    sell_lines: number;
    counted_lines: number;
    own_document: { lines: number; value: number | null };
    non_sgd: { lines: number; by_currency: Record<string, number> };
    no_total: number;
    no_date: { lines: number; value: number | null };
    before_good_history: { lines: number; value: number | null };
    repeated_lines: { groups: number; extra_lines: number; extra_value: number };
    same_subject_orders?: { threads: number; extra_orders: number; extra_value: number };
    purchases: { documents: number; lines: number };
    po_rfq_refs_available: boolean;
  };
}

export type PriceSource = 'ink' | 'curated' | 'history' | 'market' | 'other';

interface PricingCell {
  lines: number;
  within: number;
  median_gap: number | null;
}

export interface OwnerPricing {
  generated_at: string;
  total: PricingCell;
  by_source: Array<PricingCell & { source: PriceSource }>;
  monthly: Array<PricingCell & { month: string; source: PriceSource }>;
  monthly_total: Array<PricingCell & { month: string }>;
}

export type PurchaseExclusion = 'statement' | 'customer_po' | 'duplicate_document' | 'non_sgd' | 'no_total' | 'no_date';

export type RecordKind =
  | 'rfqs'
  | 'orders'
  | 'order_lines'
  | 'purchases'
  | 'pricing_lines'
  | 'system_quotes'
  | 'system_rfqs'
  // Tracking History (20261027140000_dashboard_reports.sql):
  | 'mail_events'
  | 'deleted_rfqs';

/** What to list when a number is clicked: get_owner_records(kind, from, to, filter). */
export interface DrillSpec {
  title: string;
  kind: RecordKind;
  from: string;
  to: string;
  filter?: Record<string, string | boolean>;
  /** One sentence on what the list is and how the headline was counted from it. */
  note?: string;
  /**
   * For a filter added by 20261027160000_dashboard_followups.sql: before that
   * migration is applied, get_owner_records ignores the filter and returns
   * `ignoredTotal` records instead of `expected`. When it does, the dialog
   * shows `fallback` (a list the deployed SQL can serve) rather than a list
   * that is silently not what its title says.
   */
  unlessIgnored?: { ignoredTotal: number; expected: number; fallback: DrillSpec };
}

/**
 * The list to show instead when the database ignored a drill's newer filter
 * (see DrillSpec.unlessIgnored); null when the result is what was asked for.
 * The check is exact: a filtered list is a subset of the unfiltered one, so a
 * total equal to the unfiltered total can only differ from `expected` when
 * the filter was not applied.
 */
export function ignoredFilterFallback(spec: DrillSpec, total: number): DrillSpec | null {
  const u = spec.unlessIgnored;
  if (!u || u.ignoredTotal === u.expected) return null;
  return total === u.ignoredTotal ? u.fallback : null;
}

/**
 * The lines of one PO row from an 'orders' list: the parent's window and line
 * filters, so the lines add up to the PO row's total.
 */
export function poLinesDrill(parent: DrillSpec, row: Record<string, unknown>): DrillSpec {
  const filter: Record<string, string | boolean> = { order_key: String(row.order_key) };
  if (row.customer_key != null) filter.customer_key = String(row.customer_key);
  for (const key of ['category', 'item_key'] as const) {
    if (parent.filter?.[key] != null) filter[key] = parent.filter[key];
  }
  return { title: `PO ${String(row.order_number ?? row.order_key)}`, kind: 'order_lines', from: parent.from, to: parent.to, filter };
}

export interface RecordsResult {
  kind: RecordKind;
  total: number;
  limit: number;
  sum?: number | null;
  rows: Array<Record<string, unknown>>;
}

export const SOURCE_LABELS: Record<PriceSource, string> = {
  ink: 'Ink & toner price list',
  curated: 'Curated price list',
  history: 'Past quotes & orders',
  market: 'Market search (web)',
  other: 'No source / other',
};

export const PURCHASE_EXCLUSION_LABELS: Record<PurchaseExclusion, string> = {
  statement: 'Statements of account (not purchases)',
  customer_po: 'Customer POs filed as purchases',
  duplicate_document: 'Second copy of a purchase already counted',
  non_sgd: 'Not in SGD',
  no_total: 'No total on the line',
  no_date: 'No date on the document',
};

// ---------------------------------------------------------------- dates

/** '2026-04-01' -> '2026 Q2'. */
export function quarterLabel(isoDate: string): string {
  const [year, month] = isoDate.split('-').map(Number);
  return `${year} Q${Math.floor((month - 1) / 3) + 1}`;
}

/** '2026-05-17' -> '2026-04-01'. */
function quarterStart(isoDate: string): string {
  const [year, month] = isoDate.split('-').map(Number);
  return `${year}-${String(Math.floor((month - 1) / 3) * 3 + 1).padStart(2, '0')}-01`;
}

/** Last day of the quarter that starts on (or contains) isoDate. */
export function quarterEnd(isoDate: string): string {
  const [year, month] = quarterStart(isoDate).split('-').map(Number);
  const end = new Date(Date.UTC(year, month - 1 + 3, 0));
  return end.toISOString().slice(0, 10);
}

/** Last day of the month isoDate is in. */
export function monthEnd(isoDate: string): string {
  const [year, month] = isoDate.split('-').map(Number);
  return new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10);
}

/** '2026-04-01' -> 'Apr 26'. */
export function monthLabel(isoDate: string): string {
  const [year, month] = isoDate.split('-').map(Number);
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month - 1]} ${String(year).slice(2)}`;
}

/** First day of the period of `quarters` whole quarters ending with today's. */
export function periodStart(today: string, quarters: number): string {
  const [year, month] = quarterStart(today).split('-').map(Number);
  const index = year * 4 + (month - 1) / 3 - (quarters - 1);
  return `${Math.floor(index / 4)}-${String((index % 4) * 3 + 1).padStart(2, '0')}-01`;
}

export type PeriodKey = 'full4' | 'qtd' | 'last4' | 'full8' | 'all';

/** The period options both Analytics tabs offer, in menu order; periodRange() turns one into dates. */
export const PERIOD_OPTIONS: Array<{ value: PeriodKey; label: string }> = [
  { value: 'full4', label: 'Last 4 full quarters' },
  { value: 'last4', label: 'Last 4 quarters incl. this one' },
  { value: 'qtd', label: 'This quarter to date' },
  { value: 'full8', label: 'Last 8 full quarters' },
  { value: 'all', label: 'Since Jul 2024' },
];

const addDays = (iso: string, days: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/** The period selector's options as Singapore date ranges. */
export function periodRange(key: PeriodKey, today: string): { from: string; to: string } {
  const lastFullEnd = addDays(quarterStart(today), -1);
  switch (key) {
    case 'qtd':
      return { from: quarterStart(today), to: today };
    case 'last4':
      return { from: periodStart(today, 4), to: today };
    case 'full8':
      return { from: periodStart(today, 9), to: lastFullEnd };
    case 'all':
      return { from: '2024-07-01', to: today };
    case 'full4':
    default:
      return { from: periodStart(today, 5), to: lastFullEnd };
  }
}

export const singaporeToday = (now: Date = new Date()): string => new Date(now.getTime() + 8 * 3600 * 1000).toISOString().slice(0, 10);

/** '2025-10-01'..'2026-09-30' -> '2025 Q4 - 2026 Q3'. */
export function windowLabel(from: string, to: string): string {
  const a = quarterLabel(from);
  const b = quarterLabel(to);
  return a === b ? a : `${a} - ${b}`;
}

/** '2025-10-01', '2026-09-30' -> '1 Oct 2025 to 30 Sep 2026'; the same year is not repeated. */
export function formatDateRange(from: string, to: string): string {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const parts = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return { y, label: `${d} ${MONTHS[m - 1]}` };
  };
  const a = parts(from);
  const b = parts(to);
  if (from === to) return `${b.label} ${b.y}`;
  return a.y === b.y ? `${a.label} to ${b.label} ${b.y}` : `${a.label} ${a.y} to ${b.label} ${b.y}`;
}

/**
 * A failed call in words an owner can act on. The analytics RPCs run under a
 * short statement timeout, so a busy database shows up as a timeout, not as
 * missing data.
 */
export function describeLoadError(message: string | null | undefined): string {
  const m = message ?? '';
  if (/statement timeout|canceling statement|timed? ?out|504/i.test(m)) return 'The database took too long to answer - it is probably busy. Try again in a minute.';
  if (/failed to fetch|networkerror|network request failed|load failed/i.test(m)) return 'Could not reach the server. Check the internet connection and try again.';
  return m ? `The server said: ${m}` : 'Unknown error.';
}

// ---------------------------------------------------------------- numbers

/** Percent change from `prior` to `current`, one decimal; null when prior is 0 or missing. */
export function pctChange(current: number | null | undefined, prior: number | null | undefined): number | null {
  if (current == null || prior == null || prior === 0) return null;
  return Math.round((1000 * (current - prior)) / prior) / 10;
}

/** Share in percent, one decimal; null on an empty denominator. */
export function share(part: number | null | undefined, whole: number | null | undefined): number | null {
  if (part == null || !whole) return null;
  return Math.round((1000 * part) / whole) / 10;
}

/** S$1.2M, S$345k, S$9.8k, S$980. */
export function formatSgdCompact(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '-';
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}S$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 1 : 2)}M`;
  if (abs >= 10_000) return `${sign}S$${Math.round(abs / 1000)}k`;
  if (abs >= 1000) return `${sign}S$${(abs / 1000).toFixed(1)}k`;
  return `${sign}S$${Math.round(abs)}`;
}

/** S$1,234.50 in full, for tables a reader checks against a PO. */
export function formatSgd(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '-';
  return `S$${Number(value).toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Clock durations: '12 min', '3.5 h', '27 h', '4.2 days'. */
export function formatClock(minutes: number | null | undefined): string {
  if (minutes == null || Number.isNaN(minutes)) return '-';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 10) return `${hours.toFixed(1)} h`;
  if (hours < 48) return `${Math.round(hours)} h`;
  return `${(hours / 24).toFixed(1)} days`;
}

export const formatPct = (value: number | null | undefined, digits = 0): string =>
  value == null || Number.isNaN(value) ? '-' : `${value.toFixed(digits)}%`;

/** '+12%' / '-3%' / '0%'. */
function formatDelta(value: number | null | undefined): string {
  if (value == null) return '';
  const rounded = Math.round(value);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

// ---------------------------------------------------------------- conversion

/**
 * Orders per 100 RFQs answered by email - approximate, since an order is not
 * matched to the quote it answered. Customers where the ratio would mislead
 * get a reason instead of a number.
 */
export function conversion(customerKey: string, answered: number, orders: number): { value: number | null; note: string | null } {
  if (customerKey === 'sevenseasgroup.com') {
    return { value: null, note: 'n/a - Seven Seas quotes mostly go through their portal, not email replies' };
  }
  if (customerKey === 'arcmarine.com') {
    return { value: null, note: 'n/a - ARC quotes mostly go through their Archangel portal, not email replies' };
  }
  if (answered < 5) return { value: null, note: answered === 0 ? 'no email replies on file' : 'too few replies to rate' };
  const value = Math.round((1000 * orders) / answered) / 10;
  if (customerKey === 'fujifts.com') {
    return { value, note: 'Fuji also sends standing orders that answer no RFQ, so this overstates conversion' };
  }
  if (orders > answered) {
    return { value, note: 'More orders than emailed quotes: this customer also orders without an emailed quote (portal, phone or repeat orders)' };
  }
  return { value, note: null };
}

// ---------------------------------------------------------------- customers

export type CustomerStatus = 'new' | 'active' | 'quiet' | 'rfqs_only';

/** New: first order ever falls in the period. Quiet: no order for 90+ days. */
export function customerStatus(c: Pick<CustomerRow, 'first_order' | 'last_order' | 'orders'>, periodFrom: string, today: string): CustomerStatus {
  if (!c.last_order) return 'rfqs_only';
  if (daysBetween(c.last_order, today) > 90) return 'quiet';
  if (c.first_order && c.first_order >= periodFrom) return 'new';
  return 'active';
}

function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(`${toIso.slice(0, 10)}T00:00:00Z`) - Date.parse(`${fromIso.slice(0, 10)}T00:00:00Z`)) / 86_400_000);
}

/** Revenue trend points for a sparkline, gaps kept as null. */
export function sparkPoints(c: Pick<CustomerRow, 'quarterly'>): Array<number | null> {
  return (c.quarterly ?? []).map((q) => (q.revenue == null ? null : Number(q.revenue)));
}

// ---------------------------------------------------------------- charts

/** A 7 x 24 grid (Mon..Sun x 00..23) of counts from sparse cells, with the max for scaling. */
export function heatGrid(cells: HeatCell[]): { grid: number[][]; max: number; total: number } {
  const grid = Array.from({ length: 7 }, () => Array(24).fill(0) as number[]);
  let max = 0;
  let total = 0;
  for (const c of cells) {
    if (c.dow < 1 || c.dow > 7 || c.hour < 0 || c.hour > 23) continue;
    grid[c.dow - 1][c.hour] += c.n;
    total += c.n;
    max = Math.max(max, grid[c.dow - 1][c.hour]);
  }
  return { grid, max, total };
}

/** Share of the cells outside Mon-Fri 09:00-18:00. */
export function outsideOfficeShare(cells: HeatCell[]): number | null {
  let outside = 0;
  let total = 0;
  for (const c of cells) {
    total += c.n;
    if (c.dow > 5 || c.hour < 9 || c.hour >= 18) outside += c.n;
  }
  return share(outside, total);
}

/** Monthly accuracy for the ink list against every other source, in percent. */
export function pricingTrend(p: OwnerPricing): Array<{ month: string; ink: number | null; other: number | null; all: number | null; inkLines: number; otherLines: number }> {
  const months = new Map<string, { ink: PricingCell; other: PricingCell }>();
  const empty = (): PricingCell => ({ lines: 0, within: 0, median_gap: null });
  for (const row of p.monthly) {
    const m = months.get(row.month) ?? { ink: empty(), other: empty() };
    const bucket = row.source === 'ink' ? m.ink : m.other;
    bucket.lines += row.lines;
    bucket.within += row.within;
    months.set(row.month, m);
  }
  return [...months.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, m]) => ({
      month,
      ink: share(m.ink.within, m.ink.lines),
      other: share(m.other.within, m.other.lines),
      all: share(m.ink.within + m.other.within, m.ink.lines + m.other.lines),
      inkLines: m.ink.lines,
      otherLines: m.other.lines,
    }));
}

/** Accuracy over the months from `from` to `to` (inclusive), summed from the monthly rows. */
export function pricingAccuracy(p: OwnerPricing, from: string, to: string, source?: PriceSource): { lines: number; within: number; pct: number | null } {
  let lines = 0;
  let within = 0;
  for (const row of p.monthly) {
    if (row.month < from.slice(0, 7) + '-01' || row.month > to) continue;
    if (source && row.source !== source) continue;
    lines += row.lines;
    within += row.within;
  }
  return { lines, within, pct: share(within, lines) };
}

// ---------------------------------------------------------------- export

/** RFC 4180 CSV of the given columns. */
export function toCsv(rows: Array<Record<string, unknown>>, columns: Array<{ key: string; label: string }>): string {
  const cell = (v: unknown): string => {
    if (v == null) return '';
    const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [columns.map((c) => cell(c.label)).join(','), ...rows.map((r) => columns.map((c) => cell(r[c.key])).join(','))].join('\r\n');
}

// ---------------------------------------------------------------- summary

export interface ExecTile {
  id: string;
  label: string;
  value: string;
  delta?: { text: string; direction: 'up' | 'down' | 'flat'; good: boolean | null } | null;
  detail: string;
  drill?: DrillSpec;
  /** The figure is still loading, or its call failed (the tile offers a retry). */
  status?: 'loading' | 'error';
}

const deltaOf = (value: number | null, higherIsGood: boolean): ExecTile['delta'] =>
  value == null
    ? null
    : {
        text: `${formatDelta(value)} vs prior year`,
        direction: Math.round(value) > 0 ? 'up' : Math.round(value) < 0 ? 'down' : 'flat',
        good: Math.round(value) === 0 ? null : value > 0 === higherIsGood,
      };

/**
 * The executive strip: fixed windows (last 4 full quarters against the 4
 * before, and quarter to date against the same days last year), so the
 * headline does not move with the period selector below it.
 */
export function execTiles(data: OwnerAnalytics, pricing: OwnerPricing | null, pricingError: string | null = null): ExecTile[] {
  const t = data.summary.trailing;
  const p = data.summary.prior;
  const q = data.summary.qtd;
  const ly = data.summary.qtd_last_year;
  const span = windowLabel(t.from, t.to);
  const accuracy = pricing ? pricingAccuracy(pricing, t.from, t.to) : null;
  const inkAccuracy = pricing ? pricingAccuracy(pricing, t.from, t.to, 'ink') : null;

  return [
    {
      id: 'revenue',
      label: `Revenue, ${span}`,
      value: formatSgdCompact(t.revenue),
      delta: deltaOf(pctChange(t.revenue, p.revenue), true),
      detail: `Quarter to date ${formatSgdCompact(q.revenue)} vs ${formatSgdCompact(ly.revenue)} same days last year`,
      drill: { title: `Purchase orders, ${span}`, kind: 'orders', from: t.from, to: t.to, note: 'Every PO counted as revenue: SGD lines with a total and a date, DBR’s own documents excluded.' },
    },
    {
      id: 'orders',
      label: 'Purchase orders',
      value: t.orders.toLocaleString('en-SG'),
      delta: deltaOf(pctChange(t.orders, p.orders), true),
      detail: `Average order ${formatSgdCompact(t.orders ? t.revenue / t.orders : null)} · ${t.customers} customers ordered`,
      drill: { title: `Purchase orders, ${span}`, kind: 'orders', from: t.from, to: t.to },
    },
    {
      id: 'rfqs',
      label: 'RFQs received',
      value: t.rfqs.toLocaleString('en-SG'),
      delta: null,
      detail: `${share(t.after_hours, t.rfqs) ?? '-'}% arrived outside office hours. Not compared with last year: the mailbox holds few RFQs before Apr 2025.`,
      drill: { title: `RFQs received, ${span}`, kind: 'rfqs', from: t.from, to: t.to, note: 'Every RFQ thread in the sales mailbox, by the time the first email arrived (Singapore time).' },
    },
    {
      id: 'answered',
      label: 'Answered by email',
      value: formatPct(share(t.answered, t.rfqs)),
      delta: null,
      detail: `${t.answered.toLocaleString('en-SG')} of ${t.rfqs.toLocaleString('en-SG')} · ${formatPct(share(t.via_system, t.via_system + t.via_manual))} sent through the system. Portal quotes (Seven Seas, some Wrist) have no email reply.`,
      drill: { title: `RFQs with no email reply, ${span}`, kind: 'rfqs', from: t.from, to: t.to, filter: { answered: false }, note: 'RFQ threads where no reply from DBR was found in the mailbox. Many were answered in a customer portal or by phone.' },
    },
    {
      id: 'turnaround',
      label: 'Median time to first reply',
      value: formatClock(t.speed.median_minutes),
      delta: null,
      detail: `Clock time, RFQs arriving Mon-Fri 09:00-18:00. ${formatPct(t.speed.pct_within_1h)} within 1 h, ${formatPct(t.speed.pct_within_4h)} within 4 h (${t.speed.answered.toLocaleString('en-SG')} replies).`,
      drill: {
        title: `Office-hours RFQs answered, ${span}`,
        kind: 'rfqs',
        from: t.from,
        to: t.to,
        filter: { office: true, answered: true },
        note: 'RFQs that arrived Mon-Fri 09:00-18:00 Singapore time and got an email reply; the wait is clock time from arrival to the first reply.',
      },
    },
    {
      id: 'accuracy',
      label: 'Quote lines sent at the system’s price',
      value: accuracy ? formatPct(accuracy.pct) : pricingError ? '-' : '…',
      status: accuracy ? undefined : pricingError ? 'error' : 'loading',
      delta: null,
      detail: accuracy
        ? `${accuracy.within.toLocaleString('en-SG')} of ${accuracy.lines.toLocaleString('en-SG')} priced lines within tolerance · ink & toner list ${formatPct(inkAccuracy?.pct ?? null)}`
        : pricingError
          ? `Could not load pricing accuracy. ${describeLoadError(pricingError)}`
          : 'Loading pricing accuracy',
      drill: { title: `Priced lines on quotes sent, ${span}`, kind: 'pricing_lines', from: t.from, to: t.to, note: 'Each line sent with a system suggestion. Within tolerance = the price sent is within max(2%, a minimum by price band) of the suggestion.' },
    },
    {
      id: 'concentration',
      label: 'Largest customer’s share',
      value: formatPct(t.top_customer?.share ?? null),
      delta: null,
      detail: `${t.top_customer?.name ?? '-'} · top two customers ${formatPct(t.top2_share)} of revenue`,
      drill: t.top_customer
        ? { title: `${t.top_customer.name}: purchase orders, ${span}`, kind: 'orders', from: t.from, to: t.to, filter: { customer_key: t.top_customer.customer_key } }
        : undefined,
    },
  ];
}

// ---------------------------------------------------------------- key points

/** Three to five plain sentences an owner can read before the charts. */
export function keyPoints(data: OwnerAnalytics): string[] {
  const t = data.summary.trailing;
  const p = data.summary.prior;
  const points: string[] = [];
  const growth = pctChange(t.revenue, p.revenue);
  if (growth != null) {
    points.push(
      `Revenue for ${windowLabel(t.from, t.to)} was ${formatSgdCompact(t.revenue)}, ${growth >= 0 ? 'up' : 'down'} ${Math.abs(Math.round(growth))}% on the four quarters before (${formatSgdCompact(p.revenue)}), from ${t.orders.toLocaleString('en-SG')} purchase orders.`,
    );
  }
  if (t.top_customer && t.top2_share != null) {
    points.push(`${t.top_customer.name} alone brought ${formatPct(t.top_customer.share)} of that revenue; the top two customers ${formatPct(t.top2_share)}.`);
  }
  const quiet = data.quiet_customers.items.filter((c) => (c.revenue_12m_before_last ?? 0) > 0);
  if (quiet.length) {
    const value = quiet.reduce((sum, c) => sum + Number(c.revenue_12m_before_last ?? 0), 0);
    points.push(
      `${data.quiet_customers.total} customer${data.quiet_customers.total === 1 ? ' has' : 's have'} not ordered in 90+ days; in their last year of ordering they were worth ${formatSgdCompact(value)} (largest: ${quiet[0].name}).`,
    );
  }
  if (t.rfqs) {
    points.push(
      `${formatPct(share(t.after_hours, t.rfqs))} of RFQs arrive outside office hours. For those arriving in office hours the median first reply is ${formatClock(t.speed.median_minutes)}, and ${formatPct(t.speed.pct_within_4h)} are answered within 4 hours.`,
    );
  }
  return points;
}

// ---------------------------------------------------------------- coverage

export interface CoverageItem {
  label: string;
  detail: string;
  level: 'info' | 'gap' | 'check';
  drill?: DrillSpec;
}

const plural = (n: number, word: string): string => `${n.toLocaleString('en-SG')} ${word}${n === 1 ? '' : 's'}`;

const day = (iso: string | null | undefined): string => {
  if (!iso) return 'never';
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00+08:00` : iso);
  return d.toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Singapore' });
};

/** What every number covers, what it leaves out, and the records behind each gap. */
export function coverageItems(data: OwnerAnalytics): CoverageItem[] {
  const c = data.coverage;
  const { from, to } = data.range;
  const all = { from: '2000-01-01', to: data.range.today };
  const items: CoverageItem[] = [
    {
      label: 'RFQs and replies',
      level: 'info',
      detail: `Every RFQ thread in the sales mailbox from ${day(c.threads_since)}, scanned through ${day(c.mailbox_scanned_through)}. Before go-live (Apr 2025) the mailbox holds only ${c.threads_pre_go_live} RFQs and no replies - quotes then went out from another address - so earlier quarters are shown but not compared. Replies are on file from ${day(c.replies_since)}.`,
    },
    {
      label: 'Turnaround',
      level: 'info',
      detail: 'Clock time from the RFQ arriving to DBR’s first email reply, for RFQs arriving Mon-Fri 09:00-18:00 Singapore time (public holidays count as working days). Overnight and weekend RFQs are shown on the heatmap, not in the median.',
    },
    {
      label: 'Revenue',
      level: 'info',
      detail: `Purchase orders read from email: ${c.counted_lines.toLocaleString('en-SG')} of ${c.sell_lines.toLocaleString('en-SG')} PO lines count (SGD, with a total and an order date). Orders are on file from ${day(c.orders_since)}, but history is complete only from ${day(c.orders_good_from)}. Last order ${day(c.orders_last)}; mail read through ${day(c.orders_extracted_through)}.`,
    },
    {
      label: 'Lines with no order date',
      level: 'gap',
      detail: `${plural(c.no_date.lines, 'line')} worth ${formatSgdCompact(c.no_date.value)} are not in any month or quarter.`,
      drill: { title: 'PO lines with no order date', kind: 'order_lines', ...all, filter: { reason: 'no_date' } },
    },
    {
      label: 'DBR’s own documents',
      level: 'info',
      detail: `In the selected period ${plural(c.own_document.lines, 'line')} (${formatSgdCompact(c.own_document.value)}) are DBR Systems documents (our own quotes or POs), not customer orders, and are left out.`,
      drill: { title: 'Lines from DBR’s own documents', kind: 'order_lines', from, to, filter: { reason: 'own_document' } },
    },
    {
      label: 'Other currencies',
      level: 'gap',
      detail: `In the selected period ${plural(c.non_sgd.lines, 'line')} not in SGD (${Object.entries(c.non_sgd.by_currency).map(([k, n]) => `${k} ${n}`).join(', ') || 'none'}) are counted here, not converted.`,
      drill: { title: 'PO lines not in SGD', kind: 'order_lines', from, to, filter: { reason: 'non_sgd' } },
    },
    {
      label: 'Lines with no total',
      level: 'gap',
      detail: `In the selected period ${plural(c.no_total, 'line')} had no amount the extractor could read.`,
      drill: { title: 'PO lines with no total', kind: 'order_lines', from, to, filter: { reason: 'no_total' } },
    },
    {
      label: 'Identical lines on one PO',
      level: 'check',
      detail: `${c.repeated_lines.groups} POs list the same item at the same total more than once (${c.repeated_lines.extra_lines} extra lines, ${formatSgdCompact(c.repeated_lines.extra_value)}). They are counted - a PO can repeat a line - but a line read twice would overstate revenue, so check a few against the PO.`,
      drill: { title: 'Identical lines on the same PO', kind: 'order_lines', ...all, filter: { reason: 'repeated' } },
    },
    ...(c.same_subject_orders && c.same_subject_orders.threads > 0
      ? [
          {
            label: 'One email thread, more than one PO number',
            level: 'check' as const,
            detail: `In the selected period ${c.same_subject_orders.threads} email threads carry more than one PO number (${c.same_subject_orders.extra_orders} extra POs). Some are revised POs (Wrist sng-...-1 then -2), some one PO read under two spellings (625111894P and PO0062511189). All are counted; up to ${formatSgdCompact(c.same_subject_orders.extra_value)} may be counted twice.`,
            drill: { title: 'POs that share an email thread with another PO', kind: 'orders' as const, from, to, filter: { reason: 'same_subject' }, note: 'Grouped by email subject. Where two rows are one order, revenue counts it twice.' },
          },
        ]
      : []),
    {
      label: 'Older orders',
      level: 'info',
      detail: `${c.before_good_history.lines} lines (${formatSgdCompact(c.before_good_history.value)}) are dated before ${day(c.orders_good_from)}, when few POs reached this mailbox.`,
      drill: { title: `PO lines before ${day(c.orders_good_from)}`, kind: 'order_lines', ...all, filter: { reason: 'before_good_history' } },
    },
    {
      label: 'Pricing accuracy',
      level: 'info',
      detail: 'Lines on quotes the system sent that carry a system suggestion (from go-live in Apr 2025). Lines quoted entirely by hand outside the system are not in it.',
    },
    {
      label: 'Costs',
      level: 'gap',
      detail: `Only DBR purchases whose documents arrived by email (${plural(c.purchases.documents, 'document')}, ${plural(c.purchases.lines, 'line')} in the selected period, excluded ones included) - partial, so no margin is worked out from them.`,
      drill: { title: 'All purchase documents, including excluded ones', kind: 'purchases', from, to, filter: { reason: 'all' } },
    },
    {
      label: 'Exact Wrist win rate',
      level: c.po_rfq_refs_available && data.wrist_win_rate.available ? 'info' : 'gap',
      detail:
        c.po_rfq_refs_available && data.wrist_win_rate.available
          ? `Wrist POs that quote a Wrist RFQ number (${data.wrist_win_rate.linked_pos} linked POs).`
          : 'Coming: Wrist prints its RFQ number on each PO; once those references are read into the system, the page shows exactly which quoted Wrist RFQs became orders. Until then only the approximate orders-per-RFQ ratio is shown.',
    },
    {
      label: 'Last refresh',
      level: 'info',
      detail: `Figures computed ${new Date(data.generated_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}. Latest RFQ ingested by the system ${day(c.system_rfqs_last)}.`,
    },
  ];
  return items;
}
